import type { ComputedRef } from 'vue'
import type { CustomerKeyValueRow, CustomerQuizAnswer } from '@entities/customer'

import { useCustomerStore } from '@entities/customer'

interface SectionCustomerDetailsReturn {
  hasDetails: ComputedRef<boolean>
  refAnswers: ComputedRef<CustomerQuizAnswer[]>
  refTutorFeedback: ComputedRef<CustomerKeyValueRow[]>
  refSettings: ComputedRef<CustomerKeyValueRow[]>
  refIpInfo: ComputedRef<CustomerKeyValueRow[]>
  refMarketingData: ComputedRef<CustomerKeyValueRow[]>
  refExperiments: ComputedRef<CustomerKeyValueRow[]>
}

/** Settings values arrive exactly as stored, so nested objects print as JSON like the legacy table did. */
const toDisplay = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  if (typeof value === 'object') return JSON.stringify(value, null, 2)

  return String(value)
}

const toRows = (source: object | null | undefined, skip: string[] = []): CustomerKeyValueRow[] =>
  Object.entries(source ?? {})
    .filter(([key]) => !skip.includes(key))
    .map(([key, value]) => ({ key, value: toDisplay(value) }))

const ANSWERS_KEY = 'answers'

/** The Details tab — quiz answers, tutor feedback and the raw maps of `customers.details`. */
export const useSectionCustomerDetails = (): SectionCustomerDetailsReturn => {
  const customerStore = useCustomerStore()

  const hasDetails = computed<boolean>(() => customerStore.hasDetails)

  const refAnswers = computed<CustomerQuizAnswer[]>(() => {
    const answers = customerStore.getDetails?.settings?.answers

    return Array.isArray(answers) ? answers : []
  })

  const refTutorFeedback = computed<CustomerKeyValueRow[]>(() => {
    const feedback = customerStore.getDetails?.settings?.tutor_feedback

    return feedback && typeof feedback === 'object' && !Array.isArray(feedback) ? toRows(feedback) : []
  })

  const refSettings = computed<CustomerKeyValueRow[]>(() => toRows(customerStore.getDetails?.settings, [ANSWERS_KEY]))
  const refIpInfo = computed<CustomerKeyValueRow[]>(() => toRows(customerStore.getDetails?.ip_info))
  const refMarketingData = computed<CustomerKeyValueRow[]>(() => toRows(customerStore.getDetails?.marketing_data))
  const refExperiments = computed<CustomerKeyValueRow[]>(() => toRows(customerStore.getDetails?.experiments))

  return {
    hasDetails,
    refAnswers,
    refTutorFeedback,
    refSettings,
    refIpInfo,
    refMarketingData,
    refExperiments,
  }
}
